import { observer } from 'mobx-react-lite'
import { Drawer } from 'components/Drawer'
import { MobileIntroStep } from './steps/MobileIntroStep'
import { MobileMediaSelectionStep } from './steps/MobileMediaSelectionStep'
import { MobileVisualStyleSelectionStep } from './steps/MobileVisualStyleSelectionStep'
import { MobileFontSelectionStep } from './steps/MobileFontSelectionStep'
import { MobilePreviewStep } from './steps/MobilePreviewStep'
import { MobileAnimatedImagesStep } from './steps/MobileAnimatedImagesStep'
import { MobileContentPlanStep } from './steps/MobileContentPlanStep'
import { useVisualStylesFlow, StepConfig } from '../useVisualStylesFlow'
import Styles from '../VisualStylesForExistingUsersModal.module.scss'

const MOBILE_STEP_CONFIG: StepConfig = {
  sequence: ['intro', 'media-selection', 'photo-style', 'font-pairing', 'preview', 'animated-images', 'content-plan'],
  triggerGenerationOnStep: 'font-pairing',
  singleStyleGeneration: true,
}

interface MobileVisualStylesFlowProps {
  onClose: () => void
}

export const MobileVisualStylesFlow = observer(({ onClose }: MobileVisualStylesFlowProps) => {
  const flow = useVisualStylesFlow({
    stepConfig: MOBILE_STEP_CONFIG,
    onClose,
  })

  const handleGoToStyleSelection = () => {
    flow.goToStep('photo-style')
  }

  const handleGoToFontSelection = () => {
    flow.goToStep('font-pairing')
  }

  const renderStep = () => {
    switch (flow.currentStep) {
      case 'intro':
        return <MobileIntroStep onNext={flow.handleNext} onClose={onClose} />
      case 'media-selection':
        return (
          <MobileMediaSelectionStep
            selectedMediaFileId={flow.selectedMediaFileId}
            onSelectMediaFile={flow.setSelectedMediaFileId}
            onNext={flow.handleNext}
            onBack={flow.handleBack}
            onClose={onClose}
            brandKitId={flow.brandKitId as number}
          />
        )
      case 'photo-style':
        return (
          <MobileVisualStyleSelectionStep
            selectedStyleId={flow.selectedVisualStyleId}
            onSelectStyle={flow.setSelectedVisualStyleId}
            onNext={flow.handleNext}
            onBack={flow.handleBack}
            onClose={onClose}
            visualStyles={flow.visualStyles ?? []}
            isLoading={!flow.visualStyles}
            recommendedIds={flow.recommendations.visualStyleIds}
          />
        )
      case 'font-pairing':
        return (
          <MobileFontSelectionStep
            selectedFontPairingId={flow.selectedFontPairingId}
            onSelectFontPairing={async (id) => {
              await flow.deleteAllCustomFonts()
              flow.setSelectedFontPairingId(id)
            }}
            onNext={flow.handleNext}
            onBack={flow.handleBack}
            onClose={onClose}
            isLoading={flow.recommendations.isLoading}
            recommendedIds={flow.recommendations.fontPairingIds}
          />
        )
      case 'preview':
        return (
          <MobilePreviewStep
            selectedStyle={flow.selectedStyleData}
            selectedFontPairingId={flow.selectedFontPairingId}
            brandColor={flow.brandColor}
            onNext={flow.handleNext}
            onBack={flow.handleBack}
            onClose={onClose}
            onAdjustStyle={handleGoToStyleSelection}
            onChangeFont={handleGoToFontSelection}
            isGeneratingStyle={flow.isGeneratingStyle}
            sourceImage={flow.selectedSourceImage}
            styledUrl={flow.generatedStyledUrl}
          />
        )
      case 'animated-images':
        return (
          <MobileAnimatedImagesStep
            onOptIn={(optIn) => {
              flow.setAnimatedImagesOptIn(optIn)
              flow.handleNext()
            }}
            onBack={flow.handleBack}
            onClose={onClose}
          />
        )
      case 'content-plan':
        return (
          <MobileContentPlanStep
            animatedImagesOptIn={flow.animatedImagesOptIn}
            onContentPlanChange={flow.setContentPlanValues}
            onRegenerate={flow.handleRegenerate}
            onBack={flow.handleBack}
            onClose={onClose}
            isSaving={flow.isSaving || flow.isRegenerating}
          />
        )
    }
  }

  return (
    <div data-autopilot-system-redesign={flow.currentStep === 'intro' ? 'off' : 'on'}>
      <Drawer.Root open onOpenChange={(open) => !open && onClose()}>
        <Drawer.Portal>
          <Drawer.Overlay />
          <Drawer.Content fullHeight>
            <Drawer.InnerContent className={Styles.drawerInnerContent}>{renderStep()}</Drawer.InnerContent>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  )
})
